import { Button } from '@thanx/uikit/button'
import { Text } from '@thanx/uikit/text'
import type { PhilosophyPosture } from 'api/recoveryAi.types'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React from 'react'
import { useStyletron } from 'styletron-react'
import { merchantTheme } from 'theme'
import type { RowKey } from '../components/AgentTuningConfigModals'
import {
  buildReasoningCardStyle,
  buildTweakLinkStyle,
  REASONING_BULLET_SIZE_PX,
  REASONING_LINE_HEIGHT_PX,
  REASONING_STEP_GAP_PX,
  REASONING_TITLE_MARGIN_PX,
} from './reasoningCardStyle'
import type { ScenarioReasoningStep } from './simulatorTypes'
import { TOUR_ANCHORS } from './tourAnchors'

const t = buildTranslate('recovery_ai.sandbox.reasoning')
// Posture labels are owned by the decision-log block — reuse them so the
// applied-posture step never drifts from the rest of the sandbox.
const tPosture = buildTranslate('recovery_ai.sandbox.decision_log')

const BULLET_GAP_PX = 8
// Optically centers the check bullet against the first line of the step title.
const BULLET_OFFSET_PX = 2

type Props = {
  // Always 4 entries, from the exhaustive SCENARIO_REASONING fixture.
  steps: ScenarioReasoningStep[]
  // The merchant's LIVE generosity posture (D12). Required, not optional: the
  // card says "Applied YOUR <posture> setting", and the only alternative to a
  // real value is inventing a merchant-visible fallback string nobody approved.
  // The engine gates this card on `config` being present, which guarantees it.
  posture: PhilosophyPosture
  // Opens the matching agent-tuning config modal. Optional so a host with no
  // AgentTuningConfigModals sibling degrades to a plain, link-free checklist.
  onOpenConfigRow?: (key: RowKey) => void
}

/**
 * The wizard Testing stage's hard-coded per-scenario explainability checklist.
 *
 * Pure presentation — no fetch, no dispatch; every value arrives as a prop. It
 * REPLACES DecisionLogPanel in the engine's right column during the guided
 * phase, which is why it carries TOUR_ANCHORS.reasoning: that anchor must have
 * exactly one carrier, and DecisionLogPanel.tsx:110 claims it whenever this
 * card is absent.
 *
 *   context: NOT the AI-163/AI-212 brain-fed ReasoningPanel deleted in
 *   8e543ce16. That one rendered shared bare labels off a live `reasoning_step`
 *   event; this is frozen demo copy keyed on persona_id. There is no empty
 *   state on purpose — the fixture is exhaustive and non-empty, and an early
 *   return would silently drop the tour anchor.
 */
// Under AI-921 auto-play the host passes a FILTERED prefix, which can be short
// but can never be empty, because reasoning steps 1 and 2 anchor at canonical
// rows 1 and 2 — both on screen at the first paint. `steps.length === 0` stays
// structurally unreachable in the guided phase, pinned by
// scenarioReasoning.test.ts. Do NOT add an `if (!steps.length) return null`
// early return: [] is truthy at the engine's swap, so it would not even fire,
// and it would silently drop the tour anchor if it ever did.
const ReasoningChecklist = ({ steps, posture, onOpenConfigRow }: Props) => {
  const [css] = useStyletron()

  const stepTitle = (step: ScenarioReasoningStep): string => {
    if (step.titleInterpolation !== 'posture') return t(step.titleKey)
    return t(step.titleKey, { posture: tPosture(`posture_values.${posture}`) })
  }

  return (
    <div
      data-tour={TOUR_ANCHORS.reasoning}
      className={css(buildReasoningCardStyle())}
    >
      <div className={css({ marginBottom: `${REASONING_TITLE_MARGIN_PX}px` })}>
        {/* Still an h4, so the wizard's heading order is unchanged — only the visual
            size drops (BodyText3 16px → BodyText5 12px). */}
        <Text.BodyText5
          bold
          tag="h4"
          color="grey90"
          className={css({
            lineHeight: `${REASONING_LINE_HEIGHT_PX}px`,
            margin: 0,
          })}
        >
          {t('wizard_title')}
        </Text.BodyText5>
      </div>

      {/* Explicit role="list": Safari/VoiceOver drops the implicit list role
          from the a11y tree when list-style is removed (WebKit bug 170179), so
          re-assert it to preserve the ordered-step semantics (WCAG 1.3.1). */}
      <ol
        role="list"
        className={css({
          listStyle: 'none',
          margin: 0,
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: `${REASONING_STEP_GAP_PX}px`,
        })}
      >
        {steps.map(step => {
          // Destructured to a const so TS keeps the narrowing inside the
          // onClick closure below.
          const { tweak } = step
          return (
            <li
              key={step.id}
              className={css({
                display: 'flex',
                alignItems: 'flex-start',
                gap: `${BULLET_GAP_PX}px`,
              })}
            >
              {/* The Figma bullet is INVERTED from what shipped: a white disc with a
                  teal check on it, where `fa-check-circle` draws a solid teal disc
                  with a white check punched out. Stacking a white `fa-circle` under a
                  teal `fa-check` is how the icon library gets there without a custom
                  asset. Purely decorative — the step text carries the meaning. */}
              {/* A div, not a span: it is a positioned box rather than inline text,
                  which also keeps the step's bold title the first <span> in the
                  listitem for anything walking the subtree. */}
              <div
                className={css({
                  position: 'relative',
                  width: `${REASONING_BULLET_SIZE_PX}px`,
                  height: `${REASONING_BULLET_SIZE_PX}px`,
                  marginTop: `${BULLET_OFFSET_PX}px`,
                  flex: '0 0 auto',
                })}
                aria-hidden="true"
              >
                <i
                  className={`fa fa-circle ${css({
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    color: merchantTheme.colors.white,
                    fontSize: `${REASONING_BULLET_SIZE_PX}px`,
                  })}`}
                  aria-hidden="true"
                />
                <i
                  className={`fa fa-check ${css({
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    color: merchantTheme.colors.spearmint50,
                    fontSize: `${REASONING_BULLET_SIZE_PX}px`,
                  })}`}
                  aria-hidden="true"
                />
              </div>
              {/* flex:1 + minWidth:0 gives the mockup's hanging indent — prose
                  wraps flush under the title, not under the bullet — and lets a
                  long unbroken token wrap instead of overflowing. */}
              <Text.BodyText5
                tag="div"
                // ONE colour for BOTH halves: the Figma separates the bold step title
                // from its prose by WEIGHT, not colour. grey90 rather than the grey70
                // that colour matches, because grey70 on this card's #F2FAFA wash is
                // 4.49:1 and 12px text needs 4.5:1 (WCAG 1.4.3) — it clears no
                // large-text exemption, which starts at 18.66px bold. grey90 is 9.69:1,
                // and no lighter palette grey clears the bar on this wash. The weight
                // hierarchy the design actually relies on is untouched.
                color="grey90"
                className={css({
                  flex: 1,
                  minWidth: 0,
                  lineHeight: `${REASONING_LINE_HEIGHT_PX}px`,
                })}
              >
                <Text.BodyText5 bold color="grey90">
                  {stepTitle(step)}
                </Text.BodyText5>
                {t('title_separator')}
                {t(step.bodyKey)}
                {/* Own line, but tight against the prose above it (~2px) rather than a
                    full blank line — the Button is inline-block, so it needs the
                    wrapper to break. */}
                {onOpenConfigRow && tweak && (
                  <div className={css({ marginTop: '2px' })}>
                    <Button
                      kind="minimal"
                      onClick={() => onOpenConfigRow(tweak.rowKey)}
                      aria-haspopup="dialog"
                      overrides={{
                        BaseButton: {
                          style: buildTweakLinkStyle(palette.belizeHole),
                        },
                      }}
                    >
                      {/* No aria-label: the i18n'd visible text IS the accessible
                          name, and a label that did not contain it would break
                          WCAG 2.5.3 (label in name). The arrow is decorative. */}
                      {t(tweak.labelKey)}
                      <i
                        className={`fa fa-arrow-right ${css({
                          marginLeft: '8px',
                        })}`}
                        aria-hidden="true"
                      />
                    </Button>
                  </div>
                )}
              </Text.BodyText5>
            </li>
          )
        })}
      </ol>
    </div>
  )
}

export default ReasoningChecklist
